import { Component, OnInit, OnDestroy, Inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormBuilder, FormGroup, FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSnackBar, MatSnackBarModule } from "@angular/material/snack-bar";
import { MatDialog, MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDividerModule } from "@angular/material/divider";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { HttpClient } from "@angular/common/http";
import { Subject, debounceTime, takeUntil, firstValueFrom } from "rxjs";
import { SiteService, Site } from "../../../core/services/site.service";
import { GroupService, Group } from "../../../core/services/group.service";
import { UserService } from "../../../core/services/user.service";
import { EntityContextService } from "../../../core/services/entity-context.service";
import { AuthService } from "../../../core/services/auth.service";
import { BulkUserRegistrationDialogComponent } from "../users/bulk-user-registration-dialog.component";
import { environment } from "../../../../environments/environment";

// ─── Exit-confirmation dialog ────────────────────────────────────────────────

@Component({
    selector: "app-confirm-exit-dialog",
    standalone: true,
    imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
    template: `
        <div class="exit-dlg">
            <div class="exit-dlg-header">
                <mat-icon class="warn-icon">warning_amber</mat-icon>
                <h2>Exit wizard?</h2>
            </div>
            <mat-dialog-content>
                <p>Your progress can be saved as a draft so you can resume later.</p>
            </mat-dialog-content>
            <mat-dialog-actions align="end">
                <button mat-button [mat-dialog-close]="'continue'">Continue Wizard</button>
                <button mat-stroked-button [mat-dialog-close]="'discard'" color="warn">Discard &amp; Exit</button>
                <button mat-raised-button color="primary" [mat-dialog-close]="'save'">Save Draft &amp; Exit</button>
            </mat-dialog-actions>
        </div>
    `,
    styles: [`
        .exit-dlg { padding: 8px; min-width: 340px; }
        .exit-dlg-header { display: flex; align-items: center; gap: 12px; margin-bottom: 4px; }
        .exit-dlg-header h2 { margin: 0; font-size: 1.2rem; }
        .warn-icon { color: #f59e0b; font-size: 30px; width: 30px; height: 30px; }
        mat-dialog-content p { color: #555; margin: 8px 0; }
        mat-dialog-actions { gap: 8px; padding: 16px 24px !important; }
    `],
})
export class ConfirmExitDialogComponent {}

// ─── CSV constants & interfaces ──────────────────────────────────────────────

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const REQUIRED_COLUMNS = ["name", "surname", "identity_number", "msisdn"];
const OPTIONAL_COLUMNS = ["email", "gender", "job_description"];
const ALL_COLUMNS = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS];
const VALID_GENDERS = ["male", "female", "other", ""];
const IDENTITY_PATTERN = /^[a-zA-Z0-9]{6,50}$/;
const MSISDN_PATTERN = /^\d{7,15}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SANITISE_PATTERN = /<[^>]*>|['"`;\\]/g;

interface CsvRow {
    rowNumber: number;
    name: string; surname: string; identity_number: string; msisdn: string;
    email: string; gender: string; job_description: string;
}
interface CsvPreviewRow { rowNumber: number; name: string; surname: string; identity_number: string; status: "valid" | "invalid"; reason: string; }

// ─── Wizard interfaces ────────────────────────────────────────────────────────

interface PendingUser {
    name: string; surname: string; identity_number: string;
    email: string; msisdn: string; gender: string; job_description: string;
}

interface WizardDraft {
    step: 1 | 2 | 3 | 4;
    siteMode: "select" | "create";
    siteQuery: string;
    selectedSite: Site | null;
    newSiteName: string;
    groupMode: "select" | "create";
    selectedGroup: Group | null;
    groupName: string;
    pendingUsers: PendingUser[];
    bulkImportedCount: number;
    resolvedSiteId: number | null;
    resolvedGroupId: number | null;
}

interface CampaignDraft {
    id: number; user_id: number; entity_id: number;
    step: number; state: string; created: string; modified: string;
}

export interface OnboardingStatus {
    has_entity: boolean;
    has_site: boolean;
    has_group: boolean;
    has_users: boolean;
}

// ─── Main Component ───────────────────────────────────────────────────────────

@Component({
    selector: "app-campaign-wizard",
    standalone: true,
    imports: [
        CommonModule, ReactiveFormsModule,
        MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule,
        MatProgressBarModule, MatProgressSpinnerModule, MatSnackBarModule,
        MatDialogModule, MatTooltipModule, MatDividerModule, MatSlideToggleModule,
    ],
    template: `
        <div class="wiz">

            <!-- ── Header ── -->
            <div class="wiz-header">
                <div class="wiz-title-row">
                    <div class="wiz-icon"><mat-icon>add_circle</mat-icon></div>
                    <div>
                        <h1 class="wiz-title">Setup Wizard</h1>
                        <p class="wiz-sub">Set up your site, group, and team members</p>
                    </div>
                </div>
                <div class="header-right">
                    <button mat-stroked-button *ngIf="hasDraftRestored && step === 1 && !isFirstTimeUser" (click)="startOver()" class="start-over-btn">
                        <mat-icon>restart_alt</mat-icon> Start Over
                    </button>
                    <button mat-icon-button *ngIf="!isFirstTimeUser && step < 4" (click)="onCancel()" matTooltip="Exit wizard">
                        <mat-icon>close</mat-icon>
                    </button>
                </div>
            </div>

            <!-- ── Step indicator ── -->
            <div class="step-bar" *ngIf="step < 4">
                <div class="step-item" [class.active]="step===1" [class.done]="step>1">
                    <div class="step-circle"><mat-icon *ngIf="step>1" class="ck">check</mat-icon><span *ngIf="step<=1">1</span></div>
                    <span class="step-lbl">Site Setup</span>
                </div>
                <div class="step-line" [class.done]="step>1"></div>
                <div class="step-item" [class.active]="step===2" [class.done]="step>2">
                    <div class="step-circle"><mat-icon *ngIf="step>2" class="ck">check</mat-icon><span *ngIf="step<=2">2</span></div>
                    <span class="step-lbl">Group Setup</span>
                </div>
                <div class="step-line" [class.done]="step>2"></div>
                <div class="step-item" [class.active]="step===3" [class.done]="step>3">
                    <div class="step-circle"><mat-icon *ngIf="step>3" class="ck">check</mat-icon><span *ngIf="step<=3">3</span></div>
                    <span class="step-lbl">Add People</span>
                </div>
            </div>
            <mat-progress-bar *ngIf="step<4" mode="determinate" [value]="progressPercent" class="wiz-progress"></mat-progress-bar>

            <!-- ── Body ── -->
            <div class="wiz-body">

                <!-- ════ STEP 1: Site Setup ════ -->
                <ng-container *ngIf="step===1">
                    <div class="step-content">
                        <h2 class="step-heading"><mat-icon>location_city</mat-icon> Site Setup</h2>

                        <!-- Mode toggle — only shown when existing sites are available -->
                        <div class="mode-toggle" *ngIf="hasSites && !isLoadingSites">
                            <button [class.active]="siteMode==='select'" (click)="setSiteMode('select')">
                                <mat-icon>search</mat-icon> Use Existing
                            </button>
                            <button [class.active]="siteMode==='create'" (click)="setSiteMode('create')">
                                <mat-icon>add</mat-icon> Create New
                            </button>
                        </div>

                        <!-- Select existing -->
                        <ng-container *ngIf="siteMode==='select'">
                            <p class="step-desc">Search and select the site for this setup.</p>
                            <mat-form-field appearance="outline" class="search-field">
                                <mat-label>Search sites</mat-label>
                                <input matInput [formControl]="searchControl" placeholder="Type to search…" autocomplete="off" />
                                <mat-icon matSuffix>search</mat-icon>
                            </mat-form-field>
                            <div class="loading-row" *ngIf="isLoadingSites">
                                <mat-spinner diameter="20"></mat-spinner><span>Loading…</span>
                            </div>
                            <div class="card-list" *ngIf="!isLoadingSites">
                                <div class="sel-card" *ngFor="let s of filteredSites"
                                    [class.selected]="selectedSite?.id===s.id" (click)="selectExistingSite(s)" role="button">
                                    <mat-icon class="card-icon">business</mat-icon>
                                    <span class="card-name">{{s.name}}</span>
                                    <mat-icon class="tick" *ngIf="selectedSite?.id===s.id">check_circle</mat-icon>
                                </div>
                                <div class="empty-state" *ngIf="filteredSites.length===0 && !isLoadingSites">
                                    <mat-icon>search_off</mat-icon>
                                    <p>No sites match your search.</p>
                                </div>
                            </div>
                        </ng-container>

                        <!-- Create new -->
                        <ng-container *ngIf="siteMode==='create'">
                            <p class="step-desc">Enter a name for your new site.</p>
                            <mat-form-field appearance="outline" class="full-width">
                                <mat-label>Site name</mat-label>
                                <input matInput [formControl]="newSiteNameControl" placeholder="e.g. Cape Town Office" />
                                <mat-error *ngIf="newSiteNameControl.hasError('required')">Site name is required</mat-error>
                            </mat-form-field>
                        </ng-container>
                    </div>

                    <div class="step-actions">
                        <button mat-button *ngIf="!isFirstTimeUser" (click)="onCancel()">Cancel</button>
                        <button mat-raised-button color="primary" (click)="nextFromStep1()" [disabled]="!canStep1Proceed">
                            Next <mat-icon>arrow_forward</mat-icon>
                        </button>
                    </div>
                </ng-container>

                <!-- ════ STEP 2: Group Setup ════ -->
                <ng-container *ngIf="step===2">
                    <div class="step-content">
                        <h2 class="step-heading"><mat-icon>group</mat-icon> Group Setup</h2>
                        <div class="ctx-chips">
                            <span class="ctx-chip"><mat-icon>location_city</mat-icon>{{siteDisplayName}}</span>
                        </div>

                        <!-- Loading groups -->
                        <div class="loading-row" *ngIf="isLoadingGroups">
                            <mat-spinner diameter="20"></mat-spinner><span>Loading groups…</span>
                        </div>

                        <ng-container *ngIf="!isLoadingGroups">
                            <!-- Mode toggle — only when groups exist for this site -->
                            <div class="mode-toggle" *ngIf="hasGroups">
                                <button [class.active]="groupMode==='select'" (click)="setGroupMode('select')">
                                    <mat-icon>search</mat-icon> Use Existing
                                </button>
                                <button [class.active]="groupMode==='create'" (click)="setGroupMode('create')">
                                    <mat-icon>add</mat-icon> Create New
                                </button>
                            </div>

                            <!-- Group already created banner (back-navigation) -->
                            <div class="created-banner" *ngIf="resolvedGroupId && groupMode==='create'">
                                <mat-icon>check_circle</mat-icon>
                                Group <b>"{{groupForm.get('name')?.value}}"</b> was already created.
                            </div>

                            <!-- Select existing group -->
                            <ng-container *ngIf="groupMode==='select' && hasGroups">
                                <p class="step-desc">Select a group within <b>{{siteDisplayName}}</b> for this setup.</p>
                                <div class="card-list">
                                    <div class="sel-card" *ngFor="let g of existingGroups"
                                        [class.selected]="selectedGroup?.id===g.id" (click)="selectExistingGroup(g)" role="button">
                                        <mat-icon class="card-icon">group</mat-icon>
                                        <span class="card-name">{{g.name}}</span>
                                        <mat-icon class="tick" *ngIf="selectedGroup?.id===g.id">check_circle</mat-icon>
                                    </div>
                                </div>
                            </ng-container>

                            <!-- Create new group -->
                            <ng-container *ngIf="groupMode==='create' && !resolvedGroupId">
                                <p class="step-desc">Create a new group within <b>{{siteDisplayName}}</b>.</p>
                                <form [formGroup]="groupForm">
                                    <mat-form-field appearance="outline" class="full-width">
                                        <mat-label>Group name</mat-label>
                                        <input matInput formControlName="name" placeholder="e.g. Marketing Team" />
                                        <mat-error *ngIf="groupForm.get('name')?.hasError('required')">Group name is required</mat-error>
                                    </mat-form-field>
                                </form>
                            </ng-container>
                        </ng-container>
                    </div>

                    <div class="step-actions">
                        <button mat-button (click)="backFromStep2()" [disabled]="isCreatingGroup">
                            <mat-icon>arrow_back</mat-icon> Back
                        </button>
                        <button mat-raised-button color="primary" (click)="nextFromStep2()" [disabled]="isCreatingGroup || isLoadingGroups || !canStep2Proceed">
                            <mat-spinner diameter="18" *ngIf="isCreatingGroup" class="btn-spin"></mat-spinner>
                            <ng-container *ngIf="!isCreatingGroup">
                                {{step2ActionLabel}} <mat-icon>arrow_forward</mat-icon>
                            </ng-container>
                            <span *ngIf="isCreatingGroup">Creating…</span>
                        </button>
                    </div>
                </ng-container>

                <!-- ════ STEP 3: Add People ════ -->
                <ng-container *ngIf="step===3">
                    <div class="step-content">
                        <h2 class="step-heading"><mat-icon>people</mat-icon> Add People</h2>
                        <div class="ctx-chips">
                            <span class="ctx-chip"><mat-icon>location_city</mat-icon>{{siteDisplayName}}</span>
                            <span class="ctx-chip"><mat-icon>group</mat-icon>{{groupDisplayName}}</span>
                        </div>
                        <p class="step-desc">Add team members or skip and add them later.</p>

                        <!-- Add mode toggle -->
                        <div class="mode-toggle" *ngIf="!isCreatingUsers">
                            <button [class.active]="addMode==='individual'" (click)="setAddMode('individual')">
                                <mat-icon>person_add</mat-icon> Add Individually
                            </button>
                            <button [class.active]="addMode==='bulk'" (click)="setAddMode('bulk')">
                                <mat-icon>upload_file</mat-icon> Bulk Upload
                            </button>
                        </div>

                        <!-- ── Individual mode ── -->
                        <ng-container *ngIf="addMode==='individual' && !isCreatingUsers">
                            <form [formGroup]="userForm" class="user-form">
                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>First name</mat-label>
                                        <input matInput formControlName="name" />
                                        <mat-error *ngIf="userForm.get('name')?.hasError('required')">Required</mat-error>
                                    </mat-form-field>
                                    <mat-form-field appearance="outline">
                                        <mat-label>Last name</mat-label>
                                        <input matInput formControlName="surname" />
                                        <mat-error *ngIf="userForm.get('surname')?.hasError('required')">Required</mat-error>
                                    </mat-form-field>
                                </div>
                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>ID / Passport</mat-label>
                                        <input matInput formControlName="identity_number" />
                                        <mat-error *ngIf="userForm.get('identity_number')?.hasError('required')">Required</mat-error>
                                        <mat-error *ngIf="userForm.get('identity_number')?.hasError('minlength')">Min 6 characters</mat-error>
                                    </mat-form-field>
                                    <mat-form-field appearance="outline">
                                        <mat-label>Phone</mat-label>
                                        <input matInput formControlName="msisdn" type="tel" />
                                        <mat-error *ngIf="userForm.get('msisdn')?.hasError('required')">Required</mat-error>
                                    </mat-form-field>
                                </div>
                                <div class="form-row">
                                    <mat-form-field appearance="outline">
                                        <mat-label>Email (optional)</mat-label>
                                        <input matInput formControlName="email" type="email" />
                                        <mat-error *ngIf="userForm.get('email')?.hasError('email')">Invalid email</mat-error>
                                    </mat-form-field>
                                    <button mat-raised-button color="primary" type="button" (click)="addPendingUser()" class="add-btn">
                                        <mat-icon>person_add</mat-icon> Add Person
                                    </button>
                                </div>
                            </form>
                        </ng-container>

                        <!-- ── Bulk upload mode ── -->
                        <ng-container *ngIf="addMode==='bulk' && !isCreatingUsers">
                            <div class="bulk-ai">
                                <div class="bulk-ai-title">
                                    <mat-icon>smart_toy</mat-icon>
                                    <span>AI Bulk Mapping</span>
                                </div>
                                <p class="bulk-ai-desc">Upload a CSV and automatically map columns (you can adjust mappings before import).</p>

                                <div class="bulk-ai-status" *ngIf="bulkImportedCount > 0">
                                    <mat-icon>check_circle</mat-icon>
                                    Imported {{bulkImportedCount}} {{bulkImportedCount===1?'person':'people'}} in this wizard.
                                </div>

                                <button mat-raised-button color="primary" (click)="openAIBulkUpload()">
                                    <mat-icon>upload_file</mat-icon>
                                    {{ bulkImportedCount > 0 ? 'Import More People' : 'Upload CSV' }}
                                </button>
                            </div>
                        </ng-container>

                        <!-- User creation progress -->
                        <div class="creation-progress" *ngIf="isCreatingUsers">
                            <mat-progress-bar mode="determinate" [value]="(userProgress.current/userProgress.total)*100"></mat-progress-bar>
                            <p class="progress-lbl">Creating user {{userProgress.current}} of {{userProgress.total}}…</p>
                        </div>

                        <!-- Pending list -->
                        <div class="pending-list" *ngIf="pendingUsers.length>0 && !isCreatingUsers">
                            <mat-divider></mat-divider>
                            <h3 class="pending-title"><mat-icon>people_alt</mat-icon> To add ({{pendingUsers.length}})</h3>
                            <div class="pending-row" *ngFor="let u of pendingUsers; let i=index">
                                <div class="avatar">{{u.name.charAt(0).toUpperCase()}}{{u.surname.charAt(0).toUpperCase()}}</div>
                                <div class="pending-info">
                                    <span class="pname">{{u.name}} {{u.surname}}</span>
                                    <span class="pid">{{u.identity_number}}</span>
                                </div>
                                <button mat-icon-button (click)="removePendingUser(i)" matTooltip="Remove" class="rm-btn"><mat-icon>close</mat-icon></button>
                            </div>
                        </div>
                    </div>

                    <div class="step-actions" *ngIf="!isCreatingUsers">
                        <button mat-button (click)="backFromStep3()"><mat-icon>arrow_back</mat-icon> Back</button>
                        <div class="finish-row">
                            <button mat-stroked-button (click)="skipAndFinish()">Skip</button>
                            <button mat-raised-button color="primary" (click)="finishWizard()" [disabled]="pendingUsers.length===0 && bulkImportedCount===0">
                                <mat-icon>check</mat-icon> Finish
                                <span *ngIf="pendingUsers.length>0">({{pendingUsers.length}})</span>
                            </button>
                        </div>
                    </div>
                </ng-container>

                <!-- ════ STEP 4: Success ════ -->
                <ng-container *ngIf="step===4">
                    <div class="success-screen">
                        <mat-icon class="success-icon">check_circle</mat-icon>
                        <h2>Setup complete!</h2>
                        <p class="success-sub">Your site, group, and team are ready to go.</p>
                        <div class="success-summary">
                            <div class="sum-row"><mat-icon>location_city</mat-icon><div><span class="sum-lbl">Site</span><span class="sum-val">{{siteDisplayName}}</span></div></div>
                            <div class="sum-row"><mat-icon>group</mat-icon><div><span class="sum-lbl">Group</span><span class="sum-val">{{groupDisplayName}}</span></div></div>
                            <div class="sum-row"><mat-icon>people</mat-icon><div><span class="sum-lbl">People added</span><span class="sum-val">{{createdUsersCount}}</span></div></div>
                        </div>
                        <div class="success-actions">
                            <button mat-stroked-button (click)="startNewCampaign()"><mat-icon>add</mat-icon> New Setup</button>
                            <button mat-raised-button color="primary" (click)="goToGroups()"><mat-icon>people</mat-icon> Go to Users</button>
                        </div>
                    </div>
                </ng-container>

            </div><!-- /wiz-body -->
        </div><!-- /wiz -->
    `,
    styles: [`
        /* ── Wrapper ─── */
        .wiz { display:flex; flex-direction:column; height:100%; }

        /* ── Header ─── */
        .wiz-header { display:flex; justify-content:space-between; align-items:center; padding:20px 24px 14px; border-bottom:1px solid #f0f0f0; gap:12px; flex-shrink:0; }
        .wiz-title-row { display:flex; align-items:center; gap:14px; }
        .wiz-icon { width:44px;height:44px;border-radius:11px;background:linear-gradient(135deg,#1e3a8a,#3b82f6);display:flex;align-items:center;justify-content:center;flex-shrink:0; }
        .wiz-icon mat-icon { color:#fff;font-size:24px;width:24px;height:24px; }
        .wiz-title { margin:0;font-size:1.3rem;font-weight:700;color:#1e293b; }
        .wiz-sub { margin:2px 0 0;font-size:0.85rem;color:#64748b; }
        .header-right { display:flex;align-items:center;gap:8px;flex-shrink:0; }
        .start-over-btn { font-size:0.83rem; }

        /* ── Step bar ─── */
        .step-bar { display:flex;align-items:center;padding:16px 24px 6px;flex-shrink:0; }
        .step-item { display:flex;flex-direction:column;align-items:center;gap:5px;flex-shrink:0; }
        .step-circle { width:32px;height:32px;border-radius:50%;border:2px solid #cbd5e1;background:#fff;display:flex;align-items:center;justify-content:center;font-size:0.85rem;font-weight:600;color:#94a3b8;transition:all .2s; }
        .step-item.active .step-circle { border-color:#1e3a8a;background:#1e3a8a;color:#fff; }
        .step-item.done .step-circle { border-color:#10b981;background:#10b981;color:#fff; }
        .ck { font-size:16px!important;width:16px!important;height:16px!important; }
        .step-lbl { font-size:0.75rem;color:#94a3b8;font-weight:500;white-space:nowrap; }
        .step-item.active .step-lbl { color:#1e3a8a; }
        .step-item.done .step-lbl { color:#10b981; }
        .step-line { flex:1;height:2px;background:#e2e8f0;margin:0 6px 20px;transition:background .2s; }
        .step-line.done { background:#10b981; }
        .wiz-progress { flex-shrink:0; }

        /* ── Body ─── */
        .wiz-body { flex:1;overflow-y:auto;padding:0 24px 24px; }
        .step-content { padding-top:20px;animation:fadeUp .18s ease-out; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(5px)} to{opacity:1;transform:translateY(0)} }

        /* ── Headings ─── */
        .step-heading { display:flex;align-items:center;gap:9px;font-size:1.1rem;font-weight:700;color:#1e293b;margin:0 0 4px; }
        .step-heading mat-icon { color:#1e3a8a; }
        .step-desc { color:#64748b;font-size:0.92rem;margin:0 0 16px; }

        /* ── Mode toggle ─── */
        .mode-toggle { display:flex;gap:0;margin-bottom:16px;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;width:fit-content; }
        .mode-toggle button { display:flex;align-items:center;gap:6px;padding:8px 16px;border:none;background:#fff;color:#64748b;font-size:0.88rem;cursor:pointer;transition:background .15s,color .15s; }
        .mode-toggle button:not(:last-child) { border-right:1px solid #e2e8f0; }
        .mode-toggle button.active { background:#1e3a8a;color:#fff; }
        .mode-toggle button mat-icon { font-size:16px;width:16px;height:16px; }

        /* ── Context chips ─── */
        .ctx-chips { display:flex;gap:8px;flex-wrap:wrap;margin-bottom:14px; }
        .ctx-chip { display:inline-flex;align-items:center;gap:5px;background:#eff6ff;color:#1e3a8a;border:1px solid #bfdbfe;border-radius:20px;padding:3px 10px 3px 7px;font-size:0.83rem;font-weight:500; }
        .ctx-chip mat-icon { font-size:15px;width:15px;height:15px; }

        /* ── Selection cards ─── */
        .card-list { display:flex;flex-direction:column;gap:7px;max-height:260px;overflow-y:auto;padding-right:4px; }
        .sel-card { display:flex;align-items:center;gap:11px;padding:12px 14px;border:2px solid #e2e8f0;border-radius:9px;cursor:pointer;transition:border-color .14s,background .14s;background:#fff; }
        .sel-card:hover { border-color:#93c5fd;background:#f8fbff; }
        .sel-card.selected { border-color:#1e3a8a;background:#eff6ff; }
        .card-icon { color:#3b82f6; }
        .card-name { flex:1;font-size:0.93rem;color:#1e293b; }
        .tick { color:#1e3a8a;margin-left:auto; }

        /* ── Form fields ─── */
        .full-width { width:100%; }
        .search-field { width:100%;margin-bottom:10px; }
        .toggle-row { padding:10px 0 6px; }

        /* ── Created banner ─── */
        .created-banner { display:flex;align-items:center;gap:9px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:10px 14px;color:#15803d;font-size:0.88rem;margin-bottom:14px; }
        .created-banner mat-icon { color:#16a34a; }

        /* ── Loading ─── */
        .loading-row { display:flex;align-items:center;gap:10px;color:#64748b;font-size:0.88rem;padding:14px 0; }

        /* ── Empty state ─── */
        .empty-state { display:flex;flex-direction:column;align-items:center;padding:24px 0;color:#94a3b8;gap:7px; }
        .empty-state mat-icon { font-size:36px;width:36px;height:36px; }
        .empty-state p { font-size:0.88rem;margin:0; }

        /* ── User form ─── */
        .user-form { }
        .form-row { display:flex;gap:14px;flex-wrap:wrap;align-items:flex-start; }
        .form-row > mat-form-field { flex:1;min-width:170px; }
        .add-btn { height:56px;flex-shrink:0; }

        /* ── Drop zone ─── */
        .drop-zone { border:2px dashed #cbd5e1;border-radius:10px;padding:32px;text-align:center;cursor:pointer;transition:border-color .15s,background .15s;margin-bottom:12px; }
        .drop-zone:hover,.drop-zone.drag-over { border-color:#3b82f6;background:#f0f9ff; }
        .drop-icon { font-size:40px!important;width:40px!important;height:40px!important;color:#94a3b8;margin-bottom:8px; }
        .drop-title { font-size:1rem;font-weight:600;color:#334155;margin:0 0 4px; }
        .drop-sub { font-size:0.85rem;color:#94a3b8;margin:0 0 10px; }
        .selected-file { display:inline-flex;align-items:center;gap:6px;background:#eff6ff;color:#1e3a8a;border-radius:6px;padding:4px 10px;font-size:0.85rem;margin-top:6px; }
        .bulk-error { display:flex;align-items:center;gap:8px;color:#b91c1c;background:#fee2e2;border-radius:7px;padding:8px 12px;font-size:0.88rem;margin-bottom:10px; }
        .bulk-actions-row { display:flex;gap:10px;justify-content:flex-end; }

        /* ── Bulk preview ─── */
        .bulk-summary { display:flex;gap:10px;flex-wrap:wrap;margin-bottom:12px; }
        .chip { display:inline-flex;align-items:center;gap:5px;border-radius:20px;padding:4px 12px;font-size:0.82rem;font-weight:500; }
        .chip mat-icon { font-size:15px;width:15px;height:15px; }
        .chip-ok { background:#dcfce7;color:#15803d; }
        .chip-skip { background:#fef9c3;color:#a16207; }
        .invalid-table-wrap { margin-bottom:12px; }
        .table-label { font-size:0.85rem;color:#64748b;margin-bottom:6px; }
        .preview-table { width:100%;border-collapse:collapse;font-size:0.82rem; }
        .preview-table th { background:#f8fafc;padding:6px 10px;text-align:left;font-weight:600;color:#475569;border-bottom:1px solid #e2e8f0; }
        .preview-table td { padding:6px 10px;border-bottom:1px solid #f1f5f9;color:#334155; }
        .preview-table .reason { color:#b91c1c; }
        .bulk-preview-actions { display:flex;justify-content:space-between;gap:8px; }

        /* ── Creation progress ─── */
        .creation-progress { padding:20px 0;text-align:center;animation:fadeUp .2s ease-out; }
        .progress-lbl { color:#64748b;font-size:0.88rem;margin-top:10px; }

        /* ── Pending list ─── */
        .pending-list { margin-top:18px; }
        .pending-title { display:flex;align-items:center;gap:7px;font-size:0.92rem;font-weight:600;color:#475569;margin:14px 0 10px; }
        .pending-title mat-icon { font-size:17px;width:17px;height:17px; }
        .pending-row { display:flex;align-items:center;gap:11px;padding:9px 11px;border-radius:7px;background:#f8fafc;margin-bottom:5px;border:1px solid #e2e8f0; }
        .avatar { width:34px;height:34px;border-radius:50%;background:linear-gradient(135deg,#1e3a8a,#3b82f6);color:#fff;display:flex;align-items:center;justify-content:center;font-size:0.72rem;font-weight:700;flex-shrink:0; }
        .pending-info { flex:1;display:flex;flex-direction:column; }
        .pname { font-size:0.88rem;font-weight:600;color:#1e293b; }
        .pid { font-size:0.78rem;color:#94a3b8; }
        .rm-btn { color:#94a3b8; }
        .rm-btn:hover { color:#ef4444; }

        /* ── Actions ─── */
        .step-actions { display:flex;justify-content:space-between;align-items:center;padding-top:20px;border-top:1px solid #f0f0f0;margin-top:20px; }
        .finish-row { display:flex;gap:9px; }
        .btn-spin { display:inline-flex;margin-right:7px; }

        /* ── Success ─── */
        .success-screen { display:flex;flex-direction:column;align-items:center;padding:28px 0 12px;text-align:center;animation:fadeUp .3s ease-out; }
        .success-icon { font-size:68px!important;width:68px!important;height:68px!important;color:#10b981;margin-bottom:14px; }
        .success-screen h2 { font-size:1.5rem;font-weight:700;color:#1e293b;margin:0 0 6px; }
        .success-sub { color:#64748b;font-size:0.95rem;margin:0 0 24px; }
        .success-summary { width:100%;max-width:380px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:14px 18px;margin-bottom:24px;text-align:left; }
        .sum-row { display:flex;align-items:center;gap:13px;padding:9px 0; }
        .sum-row:not(:last-child) { border-bottom:1px solid #e2e8f0; }
        .sum-row mat-icon { color:#3b82f6;flex-shrink:0; }
        .sum-row div { display:flex;flex-direction:column; }
        .sum-lbl { font-size:0.75rem;color:#94a3b8;font-weight:500; }
        .sum-val { font-size:0.92rem;font-weight:600;color:#1e293b; }
        .success-actions { display:flex;gap:11px;flex-wrap:wrap;justify-content:center; }

        /* ── Dark mode ─── */
        :host-context(.dark) .wiz-header { border-bottom-color:#21262d; }
        :host-context(.dark) .wiz-title { color:#e6edf3; }
        :host-context(.dark) .wiz-sub { color:#8b949e; }
        :host-context(.dark) .step-circle { background:#1e2843;border-color:#3d4f6e;color:#8b949e; }
        :host-context(.dark) .step-item.active .step-circle { background:#1e3a8a;border-color:#3b82f6;color:#fff; }
        :host-context(.dark) .step-item.done .step-circle { background:#065f46;border-color:#10b981; }
        :host-context(.dark) .step-lbl { color:#8b949e; }
        :host-context(.dark) .step-item.active .step-lbl { color:#93c5fd; }
        :host-context(.dark) .step-item.done .step-lbl { color:#34d399; }
        :host-context(.dark) .step-line { background:#21262d; }
        :host-context(.dark) .step-line.done { background:#065f46; }
        :host-context(.dark) .step-heading { color:#e6edf3; }
        :host-context(.dark) .step-heading mat-icon { color:#93c5fd; }
        :host-context(.dark) .step-desc { color:#8b949e; }
        :host-context(.dark) .mode-toggle { border-color:#21262d; }
        :host-context(.dark) .mode-toggle button { background:#1e2843;color:#8b949e;border-right-color:#21262d; }
        :host-context(.dark) .mode-toggle button.active { background:#1e3a8a;color:#fff; }
        :host-context(.dark) .ctx-chip { background:#1e2843;color:#93c5fd;border-color:#3d4f6e; }
        :host-context(.dark) .sel-card { background:#1e2843;border-color:#3d4f6e; }
        :host-context(.dark) .sel-card:hover { border-color:#3b82f6;background:#1a2744; }
        :host-context(.dark) .sel-card.selected { border-color:#3b82f6;background:#172554; }
        :host-context(.dark) .card-name { color:#e6edf3; }
        :host-context(.dark) .tick { color:#93c5fd; }
        :host-context(.dark) .created-banner { background:#0a2e1f;border-color:#065f46;color:#34d399; }
        :host-context(.dark) .loading-row { color:#8b949e; }
        :host-context(.dark) .empty-state { color:#3d4f6e; }
        :host-context(.dark) .drop-zone { border-color:#3d4f6e;background:#1e2843; }
        :host-context(.dark) .drop-zone:hover,.drop-zone.drag-over { border-color:#3b82f6;background:#1a2744; }
        :host-context(.dark) .drop-title { color:#e6edf3; }
        :host-context(.dark) .drop-sub { color:#8b949e; }
        :host-context(.dark) .selected-file { background:#1e2843;color:#93c5fd; }
        :host-context(.dark) .preview-table th { background:#1e2843;color:#8b949e; }
        :host-context(.dark) .preview-table td { color:#e6edf3; }
        :host-context(.dark) .pending-row { background:#1e2843;border-color:#3d4f6e; }
        :host-context(.dark) .pname { color:#e6edf3; }
        :host-context(.dark) .step-actions { border-top-color:#21262d; }
        :host-context(.dark) .step-actions .mat-mdc-button { --mat-button-text-label-text-color: #e6edf3; }
        :host-context(.dark) .success-screen h2 { color:#e6edf3; }
        :host-context(.dark) .success-sub { color:#8b949e; }
        :host-context(.dark) .success-summary { background:#1e2843;border-color:#21262d; }
        :host-context(.dark) .sum-row:not(:last-child) { border-bottom-color:#21262d; }
        :host-context(.dark) .sum-lbl { color:#8b949e; }
        :host-context(.dark) .sum-val { color:#e6edf3; }
        :host-context(.dark) .table-label { color:#8b949e; }

        /* ── Responsive ─── */
        @media (max-width:600px) {
            .wiz-header,.wiz-body { padding-left:14px;padding-right:14px; }
            .step-bar { padding:12px 14px 4px; }
            .step-lbl { display:none; }
            .form-row { flex-direction:column;gap:0; }
            .add-btn { width:100%; }
        }
    `],
})
export class CampaignWizardComponent implements OnInit, OnDestroy {

    // ── Step ──────────────────────────────────────────────────────
    step: 1 | 2 | 3 | 4 = 1;

    // ── Step 1 ────────────────────────────────────────────────────
    existingSites: Site[] = [];
    filteredSites: Site[] = [];
    hasSites = false;
    siteMode: "select" | "create" = "select";
    searchControl = new FormControl<string>("");
    newSiteNameControl = new FormControl<string>("", Validators.required);
    selectedSite: Site | null = null;
    isLoadingSites = false;

    // ── Step 2 ────────────────────────────────────────────────────
    existingGroups: Group[] = [];
    hasGroups = false;
    groupMode: "select" | "create" = "create";
    selectedGroup: Group | null = null;
    groupForm: FormGroup;
    isLoadingGroups = false;
    isCreatingGroup = false;
    resolvedSiteId: number | null = null;
    resolvedGroupId: number | null = null;

    // ── Step 3 — individual ───────────────────────────────────────
    addMode: "individual" | "bulk" = "bulk";
    userForm: FormGroup;
    pendingUsers: PendingUser[] = [];
    bulkImportedCount = 0;
    isCreatingUsers = false;
    userProgress = { current: 0, total: 0 };
    createdUsersCount = 0;

    // ── Step 3 — bulk ─────────────────────────────────────────────
    bulkPhase: "upload" | "preview" = "upload";
    bulkFile: File | null = null;
    isDragOver = false;
    bulkError: string | null = null;
    bulkValidRows: CsvRow[] = [];
    bulkInvalidRows: CsvPreviewRow[] = [];

    // ── Misc ──────────────────────────────────────────────────────
    hasDraftRestored = false;
    private readonly draftUrl = `${environment.apiUrl}/api/v1/campaign/draft`;
    private destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<CampaignWizardComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { isFirstTimeUser: boolean; onboardingStatus?: OnboardingStatus },
        private snackBar: MatSnackBar,
        private http: HttpClient,
        private siteService: SiteService,
        private groupService: GroupService,
        private userService: UserService,
        private entityContext: EntityContextService,
        private authService: AuthService,
    ) {
        this.groupForm = this.fb.group({ name: ["", Validators.required] });
        this.userForm = this.fb.group({
            name: ["", Validators.required], surname: ["", Validators.required],
            identity_number: ["", [Validators.required, Validators.minLength(6)]],
            msisdn: ["", Validators.required], email: ["", Validators.email],
        });
    }

    get isFirstTimeUser(): boolean { return this.data?.isFirstTimeUser ?? false; }

    get progressPercent(): number { return ((this.step - 1) / 3) * 100; }

    get siteDisplayName(): string {
        return this.selectedSite?.name ?? this.newSiteNameControl.value?.trim() ?? "";
    }

    get groupDisplayName(): string {
        return this.selectedGroup?.name ?? this.groupForm.get("name")?.value ?? "";
    }

    get canStep1Proceed(): boolean {
        if (this.siteMode === "select") return !!this.selectedSite;
        return !!this.newSiteNameControl.value?.trim();
    }

    get canStep2Proceed(): boolean {
        if (this.isLoadingGroups) return false;
        if (this.groupMode === "select") return !!this.selectedGroup;
        if (this.resolvedGroupId) return true; // already created
        return this.groupForm.get("name")?.value?.trim().length > 0;
    }

    get step2ActionLabel(): string {
        if (this.resolvedGroupId) return "Continue";
        if (this.groupMode === "select") return "Continue";
        return "Create Group";
    }

    ngOnInit(): void {
        const status = this.data?.onboardingStatus;

        if (status && !status.has_site) {
            // Backend confirms no sites exist — skip API call, go straight to create mode
            this.hasSites = false;
            this.siteMode = "create";
            this.isLoadingSites = false;
        } else {
            this.isLoadingSites = true;
            this.siteService.getSites().pipe(takeUntil(this.destroy$)).subscribe({
                next: (sites) => {
                    this.existingSites = sites;
                    this.filteredSites = sites;
                    this.hasSites = sites.length > 0;
                    this.siteMode = sites.length > 0 ? "select" : "create";
                    this.isLoadingSites = false;
                },
                error: () => { this.hasSites = false; this.siteMode = "create"; this.isLoadingSites = false; },
            });
        }

        this.searchControl.valueChanges.pipe(debounceTime(150), takeUntil(this.destroy$))
            .subscribe(q => this.filterSites(q ?? ""));

        // Load backend draft
        this.http.get<CampaignDraft>(this.draftUrl, { observe: "response" })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    if (res.status === 204 || !res.body) return;
                    try { this.applyDraft(JSON.parse(res.body.state), res.body.step as 1 | 2 | 3 | 4); }
                    catch { /* malformed draft */ }
                },
                error: () => { /* 204 or no draft */ },
            });
    }

    ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

    // ── Step 1 ────────────────────────────────────────────────────

    setSiteMode(mode: "select" | "create"): void {
        this.siteMode = mode;
        this.selectedSite = null;
        if (mode === "select") this.newSiteNameControl.reset();
    }

    filterSites(q: string): void {
        const lq = q.toLowerCase().trim();
        this.filteredSites = lq ? this.existingSites.filter(s => s.name.toLowerCase().includes(lq)) : this.existingSites;
    }

    selectExistingSite(site: Site): void { this.selectedSite = site; }

    nextFromStep1(): void {
        if (!this.canStep1Proceed) return;
        if (this.siteMode === "select") {
            this.resolvedSiteId = this.selectedSite!.id;
        }
        this.saveDraft(2);
        this.enterStep2();
    }

    // ── Step 2 ────────────────────────────────────────────────────

    private async enterStep2(): Promise<void> {
        this.step = 2;
        const status = this.data?.onboardingStatus;
        const siteIdForGroups = this.resolvedSiteId ?? this.selectedSite?.id ?? null;

        // If backend confirms no groups exist in the entity at all, skip the API call
        if (status && !status.has_group) {
            this.existingGroups = [];
            this.hasGroups = false;
            this.groupMode = "create";
            return;
        }

        if (siteIdForGroups) {
            this.isLoadingGroups = true;
            try {
                const groups = await firstValueFrom(this.groupService.getGroups(siteIdForGroups));
                this.existingGroups = groups;
                this.hasGroups = groups.length > 0;
                this.groupMode = groups.length > 0 ? "select" : "create";
            } catch {
                this.existingGroups = [];
                this.hasGroups = false;
                this.groupMode = "create";
            } finally {
                this.isLoadingGroups = false;
            }
        } else {
            this.existingGroups = [];
            this.hasGroups = false;
            this.groupMode = "create";
        }
    }

    setGroupMode(mode: "select" | "create"): void {
        this.groupMode = mode;
        this.selectedGroup = null;
        if (mode === "create") this.groupForm.reset({ name: "" });
    }

    selectExistingGroup(group: Group): void { this.selectedGroup = group; }

    backFromStep2(): void { this.saveDraft(1); this.step = 1; }

    async nextFromStep2(): Promise<void> {
        if (!this.canStep2Proceed) return;

        // If selecting existing group — just proceed
        if (this.groupMode === "select" && this.selectedGroup) {
            this.resolvedGroupId = this.selectedGroup.id;
            this.saveDraft(3);
            this.step = 3;
            return;
        }

        // If group already created — just proceed
        if (this.resolvedGroupId) {
            this.saveDraft(3);
            this.step = 3;
            return;
        }

        // Create group (and site if needed)
        if (this.groupForm.invalid) { this.groupForm.markAllAsTouched(); return; }

        this.isCreatingGroup = true;
        try {
            // Create site if it's new
            if (!this.resolvedSiteId) {
                const newSite = await firstValueFrom(
                    this.siteService.createSite({ name: this.newSiteNameControl.value!.trim() })
                );
                this.resolvedSiteId = newSite.id;
                this.selectedSite = newSite;
            }
            // Create group
            const group = await firstValueFrom(
                this.groupService.createGroup({
                    name: this.groupForm.get("name")!.value.trim(),
                    site_id: this.resolvedSiteId!,
                    generate_interpretation: true,
                })
            );
            this.resolvedGroupId = group.id;
            this.saveDraft(3);
            this.step = 3;
        } catch (err: any) {
            this.snackBar.open(err?.error?.message ?? "Failed to create group. Please try again.", "Close",
                { duration: 5000, panelClass: ["error-snackbar"] });
        } finally {
            this.isCreatingGroup = false;
        }
    }

    // ── Step 3 ────────────────────────────────────────────────────

    setAddMode(mode: "individual" | "bulk"): void {
        this.addMode = mode;
        this.resetBulkUpload();
    }

    backFromStep3(): void { this.saveDraft(2); this.step = 2; }

    addPendingUser(): void {
        if (this.userForm.invalid) { this.userForm.markAllAsTouched(); return; }
        const v = this.userForm.getRawValue();
        this.pendingUsers = [...this.pendingUsers, {
            name: v.name.trim(), surname: v.surname.trim(),
            identity_number: v.identity_number.trim(), msisdn: v.msisdn?.trim() ?? "",
            email: v.email?.trim() ?? "", gender: "", job_description: "",
        }];
        this.userForm.reset({ name: "", surname: "", identity_number: "", msisdn: "", email: "" });
        this.saveDraft(3);
    }

    removePendingUser(i: number): void {
        this.pendingUsers = this.pendingUsers.filter((_, idx) => idx !== i);
        this.saveDraft(3);
    }

    async finishWizard(): Promise<void> {
        if (this.pendingUsers.length === 0) {
            this.createdUsersCount = this.bulkImportedCount;
            this.clearDraft();
            this.step = 4;
            return;
        }

        this.isCreatingUsers = true;
        this.userProgress = { current: 0, total: this.pendingUsers.length };
        this.createdUsersCount = this.bulkImportedCount;

        const siteId = this.resolvedSiteId!;
        const groupId = this.resolvedGroupId!;
        const entityId = this.entityContext.getCurrentEntity()?.id;
        const userTypeId = this.authService.isAdmin() ? 1 : 2;

        for (let i = 0; i < this.pendingUsers.length; i++) {
            const u = this.pendingUsers[i];
            this.userProgress = { current: i + 1, total: this.pendingUsers.length };
            const payload: any = {
                name: u.name, surname: u.surname, identity_number: u.identity_number,
                msisdn: u.msisdn ? Number(u.msisdn) : 0,
                email: u.email || undefined,
                gender: u.gender ? u.gender.charAt(0).toUpperCase() + u.gender.slice(1).toLowerCase() : undefined,
                job_description: u.job_description || undefined,
                site_id: siteId, group_id: groupId, user_type_id: userTypeId,
                entity_id: entityId,
            };
            Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
            try {
                await firstValueFrom(this.userService.createUser(payload));
                this.createdUsersCount++;
            } catch (err) { console.error("Failed to create user:", u.identity_number, err); }
        }

        this.isCreatingUsers = false;
        this.clearDraft();
        this.step = 4;
    }

    skipAndFinish(): void { this.clearDraft(); this.step = 4; }

    openAIBulkUpload(): void {
        const siteId = this.resolvedSiteId ?? this.selectedSite?.id ?? null;
        const groupId = this.resolvedGroupId ?? this.selectedGroup?.id ?? null;

        const dialogRef = this.dialog.open(BulkUserRegistrationDialogComponent, {
            width: "860px",
            maxWidth: "96vw",
            data: { siteId, groupId },
        });

        dialogRef.afterClosed().subscribe((result: any) => {
            const totalRegistered = typeof result === "object" && result ? Number(result.totalRegistered ?? 0) : 0;
            if (totalRegistered > 0) {
                this.bulkImportedCount += totalRegistered;
                this.saveDraft(3);
                this.snackBar.open(`Imported ${totalRegistered} ${totalRegistered === 1 ? "person" : "people"}.`, "OK",
                    { duration: 4000, panelClass: ["success-snackbar"] });
            }
        });
    }

    // ── Success ───────────────────────────────────────────────────

    goToGroups(): void { this.dialogRef.close({ navigateTo: "/dashboard/users" }); }

    startNewCampaign(): void { this.startOver(); }

    // ── Bulk upload ───────────────────────────────────────────────

    onDragOver(e: DragEvent): void { e.preventDefault(); e.stopPropagation(); this.isDragOver = true; }
    onDragLeave(e: DragEvent): void { e.preventDefault(); e.stopPropagation(); this.isDragOver = false; }
    onDrop(e: DragEvent): void {
        e.preventDefault(); e.stopPropagation(); this.isDragOver = false;
        this.handleBulkFile(e.dataTransfer?.files?.[0] ?? null);
    }
    onBulkFileSelected(e: Event): void {
        const input = e.target as HTMLInputElement;
        this.handleBulkFile(input.files?.[0] ?? null);
        input.value = "";
    }
    private handleBulkFile(file: File | null): void {
        this.bulkError = null; this.bulkFile = null;
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(".csv") && file.type !== "text/csv") {
            this.bulkError = "Only CSV files are accepted."; return;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            this.bulkError = `File exceeds the 10 MB limit (${this.formatFileSize(file.size)}).`; return;
        }
        this.bulkFile = file;
    }

    validateBulkFile(): void {
        if (!this.bulkFile) return;
        const reader = new FileReader();
        reader.onload = (e) => {
            try { this.processBulkText(e.target!.result as string); this.bulkPhase = "preview"; }
            catch (err: any) { this.bulkError = err?.message ?? "Failed to parse CSV."; }
        };
        reader.onerror = () => { this.bulkError = "Failed to read the file."; };
        reader.readAsText(this.bulkFile, "UTF-8");
    }

    private processBulkText(text: string): void {
        const lines = text.split(/\r?\n/);
        if (lines.length < 2) throw new Error("CSV is empty or has only a header row.");
        const headers = this.parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
        const missing = REQUIRED_COLUMNS.filter(c => !headers.includes(c));
        if (missing.length) throw new Error(`Missing required column(s): ${missing.join(", ")}`);
        this.bulkValidRows = []; this.bulkInvalidRows = [];
        const seenIds = new Set<string>(); const seenEmails = new Set<string>();
        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim(); if (!line) continue;
            const cells = this.parseCSVLine(line);
            const get = (col: string) => this.csvSanitise((cells[headers.indexOf(col)] ?? "").trim());
            const row: CsvRow = {
                rowNumber: i + 1, name: get("name"), surname: get("surname"),
                identity_number: get("identity_number"), msisdn: get("msisdn"),
                email: headers.includes("email") ? get("email") : "",
                gender: headers.includes("gender") ? get("gender") : "",
                job_description: headers.includes("job_description") ? get("job_description") : "",
            };
            const err = this.validateCsvRow(row, seenIds, seenEmails);
            if (err) {
                this.bulkInvalidRows.push({ rowNumber: row.rowNumber, name: row.name, surname: row.surname, identity_number: row.identity_number, status: "invalid", reason: err });
            } else {
                seenIds.add(row.identity_number.toLowerCase());
                if (row.email) seenEmails.add(row.email.toLowerCase());
                this.bulkValidRows.push(row);
            }
        }
        if (this.bulkValidRows.length === 0 && this.bulkInvalidRows.length === 0)
            throw new Error("No data rows found in CSV.");
    }

    private validateCsvRow(row: CsvRow, seenIds: Set<string>, seenEmails: Set<string>): string | null {
        if (!row.name) return "Name is required";
        if (!row.surname) return "Surname is required";
        if (!row.identity_number) return "ID/Passport is required";
        if (!IDENTITY_PATTERN.test(row.identity_number)) return "ID must be 6–50 alphanumeric chars";
        if (!row.msisdn) return "Phone (msisdn) is required";
        if (!MSISDN_PATTERN.test(row.msisdn)) return "Phone must be 7–15 digits";
        if (row.email && !EMAIL_PATTERN.test(row.email)) return "Invalid email format";
        if (row.gender && !VALID_GENDERS.includes(row.gender.toLowerCase())) return `Invalid gender: "${row.gender}"`;
        if (seenIds.has(row.identity_number.toLowerCase())) return `Duplicate ID in file: ${row.identity_number}`;
        if (row.email && seenEmails.has(row.email.toLowerCase())) return `Duplicate email in file: ${row.email}`;
        return null;
    }

    confirmBulkAdd(): void {
        const newUsers: PendingUser[] = this.bulkValidRows.map(r => ({
            name: r.name, surname: r.surname, identity_number: r.identity_number,
            msisdn: r.msisdn, email: r.email, gender: r.gender, job_description: r.job_description,
        }));
        this.pendingUsers = [...this.pendingUsers, ...newUsers];
        this.saveDraft(3);
        this.resetBulkUpload();
        this.addMode = "individual";
        this.snackBar.open(`${newUsers.length} ${newUsers.length === 1 ? "person" : "people"} added to the list.`, "OK",
            { duration: 3000, panelClass: ["success-snackbar"] });
    }

    resetBulkUpload(): void {
        this.bulkPhase = "upload"; this.bulkFile = null; this.bulkError = null;
        this.bulkValidRows = []; this.bulkInvalidRows = []; this.isDragOver = false;
    }

    downloadBulkTemplate(): void {
        const header = ALL_COLUMNS.join(",");
        const example = "John,Doe,JD123456,821234567,john.doe@example.com,Male,Software Engineer";
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([`${header}\n${example}\n`], { type: "text/csv" }));
        a.download = "campaign_people_template.csv"; a.click(); URL.revokeObjectURL(a.href);
    }

    // ── Cancel / exit ─────────────────────────────────────────────

    onCancel(): void {
        const hasData = this.selectedSite || this.newSiteNameControl.value || this.groupForm.get("name")?.value || this.pendingUsers.length > 0;
        if (!hasData) { this.dialogRef.close(null); return; }
        this.dialog.open(ConfirmExitDialogComponent, { width: "420px", maxWidth: "90vw" })
            .afterClosed().subscribe((res: string) => {
                if (res === "save") { this.saveDraft(this.step); this.dialogRef.close(null); }
                else if (res === "discard") { this.clearDraft(); this.dialogRef.close(null); }
            });
    }

    startOver(): void {
        this.clearDraft();
        this.step = 1; this.selectedSite = null; this.resolvedSiteId = null; this.resolvedGroupId = null;
        this.selectedGroup = null; this.searchControl.setValue(""); this.newSiteNameControl.reset();
        this.groupForm.reset({ name: "" });
        this.userForm.reset(); this.pendingUsers = []; this.bulkImportedCount = 0; this.hasDraftRestored = false;
        this.siteMode = this.hasSites ? "select" : "create";
        this.resetBulkUpload();
        this.filterSites("");
    }

    // ── Draft ─────────────────────────────────────────────────────

    private saveDraft(step: 1 | 2 | 3 | 4): void {
        const state: WizardDraft = {
            step, siteMode: this.siteMode, siteQuery: this.searchControl.value ?? "",
            selectedSite: this.selectedSite, newSiteName: this.newSiteNameControl.value ?? "",
            groupMode: this.groupMode, selectedGroup: this.selectedGroup,
            groupName: this.groupForm.get("name")?.value ?? "",
            pendingUsers: this.pendingUsers,
            bulkImportedCount: this.bulkImportedCount,
            resolvedSiteId: this.resolvedSiteId, resolvedGroupId: this.resolvedGroupId,
        };
        this.http.put(this.draftUrl, { step, state }).subscribe();
    }

    private applyDraft(draft: WizardDraft, step: 1 | 2 | 3 | 4): void {
        const nonTrivial = step > 1 || !!draft.selectedSite || !!draft.newSiteName || !!draft.groupName || (draft.pendingUsers?.length ?? 0) > 0;
        if (nonTrivial) this.hasDraftRestored = true;
        this.step = step;
        this.siteMode = draft.siteMode ?? "select";
        this.selectedSite = draft.selectedSite ?? null;
        this.newSiteNameControl.setValue(draft.newSiteName ?? "");
        this.groupMode = draft.groupMode ?? "create";
        this.selectedGroup = draft.selectedGroup ?? null;
        this.resolvedSiteId = draft.resolvedSiteId ?? null;
        this.resolvedGroupId = draft.resolvedGroupId ?? null;
        this.pendingUsers = draft.pendingUsers ?? [];
        this.bulkImportedCount = draft.bulkImportedCount ?? 0;
        if (draft.siteQuery) this.searchControl.setValue(draft.siteQuery, { emitEvent: false });
        this.groupForm.patchValue({ name: draft.groupName ?? "" });
        if (this.step >= 3 && !this.resolvedGroupId) this.step = 2;
    }

    private clearDraft(): void { this.http.delete(this.draftUrl).subscribe(); }

    // ── CSV helpers ───────────────────────────────────────────────

    private parseCSVLine(line: string): string[] {
        const result: string[] = []; let field = ""; let inQuotes = false; let i = 0;
        while (i < line.length) {
            const ch = line[i];
            if (inQuotes) {
                if (ch === '"' && line[i + 1] === '"') { field += '"'; i += 2; }
                else if (ch === '"') { inQuotes = false; i++; }
                else { field += ch; i++; }
            } else {
                if (ch === '"') { inQuotes = true; i++; }
                else if (ch === ',') { result.push(field); field = ""; i++; }
                else { field += ch; i++; }
            }
        }
        result.push(field); return result;
    }

    private csvSanitise(v: string): string { return v.replace(SANITISE_PATTERN, "").trim(); }

    formatFileSize(bytes: number): string {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
}
